/**
 * Selectable row for a path inside a rule group (mirrors DefaultPathListRow).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import type { VoluumPathUpdate } from '@trafficpulse/shared';

import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';
import {
  usePreviewMode,
  usePreviewRow,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import PreviewAwareWeightSlot from '@/features/flow-optimizer/components/preview/PreviewAwareWeightSlot';
import PreviewExcludedIndicator from '@/features/flow-optimizer/components/preview/PreviewExcludedIndicator';
import { FlowTagChips } from '@/features/flow-optimizer/components/preview/FlowTagChips';
import { StandardOfferCapChips } from '@/features/flow-optimizer/tabs/standard/v2/caps/StandardOfferCapChips';
import { excludedReasonText, showExcludedBadge } from '@/features/flow-optimizer/components/preview/preview-row.types';

export interface GroupPathRowProps {
  path: VoluumPathUpdate;
  selected: boolean;
  weightLocked: boolean;
  onSelect: () => void;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
}

const GroupPathRow: FC<GroupPathRowProps> = ({
  path,
  selected,
  weightLocked,
  onSelect,
  onPatch,
}) => {
  const previewMode = usePreviewMode();
  const previewRow = usePreviewRow('paths', path.id ?? '');
  const previewLocked =
    previewMode && previewRow != null && !showExcludedBadge(previewRow, Boolean(path.active));
  const gridCols = previewLocked
    ? 'grid-cols-[minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem]'
    : 'grid-cols-[minmax(0,1fr)_3.5rem_2.75rem]';

  return (
    <li
      role="option"
      aria-selected={selected}
      className={cn(
        'transition-colors outline-none',
        selected && 'bg-primary/5',
        !path.active && 'opacity-70',
      )}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        className="w-full px-2 py-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        <div className={cn('grid w-full items-center gap-x-1.5', gridCols)}>
          {/* The name keeps ≥5rem and takes the rest of the line; the chips stay
                whole and drop to a second line only when the row is too narrow. */}
            <span className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
            <span className="min-w-0 flex-[1_1_5rem] truncate text-xs font-medium leading-tight text-foreground">
              {path.name?.trim() ? path.name : 'Untitled path'}
            </span>
            <FlowTagChips layer="paths" itemId={path.id ?? ''} className="shrink-0 gap-1" />
            <StandardOfferCapChips offerIds={(path.offers ?? []).map((o) => o.offer.id)} />
            {showExcludedBadge(previewRow, Boolean(path.active)) ? (
              <PreviewExcludedIndicator
                title={excludedReasonText(previewRow)}
                row={previewRow}
                name={path.name?.trim() ? path.name : 'Untitled path'}
              />
            ) : null}
          </span>
          <div
            className={cn('flex items-center justify-end', previewLocked && 'col-span-4')}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <PreviewAwareWeightSlot
              name={path.name?.trim() ? path.name : 'Untitled path'}
              previewRow={previewRow}
              previewMode={previewMode}
              active={Boolean(path.active)}
              value={path.weight}
              onChange={(v) =>
                onPatch((p) => {
                  p.weight = v;
                })
              }
              ariaLabel={`Weight for ${path.name || path.id}`}
              forceReadOnly={!previewMode && weightLocked}
              forceReadOnlyHint="Path weight is managed by Voluum (Traffic Distribution AI on for this group)"
            />
          </div>
          <div
            className="flex items-center justify-end"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Switch
              checked={path.active}
              onCheckedChange={(v) =>
                onPatch((p) => {
                  p.active = v;
                })
              }
              aria-label={`${path.active ? 'Disable' : 'Enable'} ${path.name || path.id}`}
            />
          </div>
        </div>
      </div>
    </li>
  );
};

export default GroupPathRow;
